import { Fragment, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useRouter } from 'next/router';
import { ArrowUpRight, ChevronRight, TriangleAlert } from 'lucide-react';
import { useTranslation } from '../../lib/i18n';
import { useBasePath } from '../../lib/use-base-path';
import { useIslandState } from '../../lib/use-island-state';
import { useGenericBi } from '../../lib/use-generic-bi';
import { handleModalEscape, pushModalEscape } from '../../lib/modal-escape';
import { useIslandOpenRefresh, useIslandQuickView } from '../../lib/use-island-quickview';
import type { QuickViewRow, QuickViewTask } from '../../lib/island-quickview';
import { bucketOfTask, type AgentTaskBucket } from '../../lib/agent-task-buckets';
import { useMyAgents } from '../../lib/my-agents';
import { useAgentName } from '../../lib/use-agent-name';
import { useTaskSidePanel } from './task-side-panel/TaskSidePanelContext';
import { SharedFileViewer } from './files/SharedFileViewer';
import { TaskDetailModal } from './tasks/TaskDetailModal';
import { OutreachThreadModal } from './tasks/OutreachThreadModal';
import { queryClient, queryKeys } from '../../lib/query-client';
import type { WebTask } from '../../lib/tasks';
import { useActiveConversation } from '../chat/ActiveConversationContext';
import { useLeaveChatOnNavigate } from '../../lib/use-leave-chat';
import { Button, ScrollArea, TextButton } from './ui';
import { MaestroActionRowButton, QuickViewRowButton, WorkingRowButton } from './AgentsIslandQuickViewRow';

// The island quick-view — the "1c: stretch, then drop" morph, where the PILL IS THE PANEL'S HEADER. One
// continuous tinted surface: the pill (agent dots + state count) widens across the bar into the panel's
// full-width header row FIRST (fast, no delay), THEN the body drops open beneath it (height, delayed, with
// an overshoot), and only THEN do the task rows fade + slide in. The pill's content stays pinned to the
// trailing edge so it never slides; the standalone pill is hidden while open, so the header stands in for
// it seamlessly. Closing runs the same curve without overshoot and ~25% faster: the body collapses first,
// the header shrinks back to the pill last. Rows open the task; the footer is the app's standard dark CTA.

const INK = 'var(--sym-ink)';
const INK_2 = 'var(--ds-text-secondary)';

const PANEL_W = 335;
const EDGE_GAP = 8; // the panel never sits closer than this to a viewport edge
/** The panel's own box (Figma 13523-215388): its edge, its sides and foot, where content starts, and the gap
 * between blocks. */
const PANEL_BORDER = 1;
const PANEL_PAD = 20;
const CONTENT_TOP = 48;
const CONTENT_GAP = 20;

/** Which edge the panel is pinned to — and therefore which edge its header content hugs. */
export type IslandSide = 'left' | 'right' | 'center';
const HEADER_JUSTIFY: Record<IslandSide, 'flex-start' | 'flex-end' | 'center'> = {
  left: 'flex-start',
  right: 'flex-end',
  center: 'center',
};
const RADIUS_OPEN = 20;
const CLOSE_MS = 340; // hold the node mounted through the close curve before unmounting
const OS = 'cubic-bezier(.34,1.34,.38,1)'; // overshoot (open)
const EO = 'cubic-bezier(.35,0,.22,1)'; // ease-out (close)
const OPEN_TR = `width 167ms cubic-bezier(.3,0,.2,1), max-height 266ms ${OS} 122ms, background-color 266ms ease 122ms, border-radius 380ms ease, box-shadow 380ms ease`;
const CLOSE_TR = `width 126ms ${EO} 141ms, max-height 169ms ${EO}, background-color 169ms ${EO}, border-radius 281ms ease, box-shadow 281ms ease`;
const BODY_OPEN_TR = `opacity 200ms ease 274ms, transform 260ms ${OS} 274ms`;
const BODY_CLOSE_TR = 'opacity 90ms ease, transform 90ms ease';
const SHADOW_LIFT = '0 16px 36px rgba(var(--ds-black-rgb), 0.15), 0 2px 6px rgba(var(--ds-black-rgb), 0.06)';
const SHADOW_SHUT = '0 2px 6px rgba(var(--ds-black-rgb), 0.07)';

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}

function SkeletonRow() {
  return (
    <div style={{ display: 'flex', gap: 10, padding: '10px', alignItems: 'flex-start' }}>
      <span className="orion-shimmer" style={{ width: 20, height: 20, borderRadius: 999, flexShrink: 0 }} />
      <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <span className="orion-shimmer" style={{ height: 12, width: '75%', borderRadius: 6 }} />
        <span className="orion-shimmer" style={{ height: 10, width: '45%', borderRadius: 6 }} />
      </span>
    </div>
  );
}

/** Between rows only — the first row sits flush at the top of the list. Separator/Light (Figma 13523-215388). */
const HAIRLINE: React.CSSProperties = { borderTop: '1px solid var(--ds-separator-default)' };

/**
 * The orange strip inside the blue card — the pending-decision co-signal the blue pill itself no longer
 * carries: "{count} tasks need your action", on the island's own waiting fill, tapping through to the
 * Waiting tab. Rendered only when something actually waits.
 */
const WAITING_STRIP: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  width: '100%',
  padding: '12px 12px 12px 16px',
  minHeight: 36,
  borderRadius: 16,
  border: 'none',
  background: 'var(--island-nav-pill-waiting-bg)',
  color: INK,
  font: 'inherit',
  fontSize: 14,
  lineHeight: '18px',
  letterSpacing: '-0.02em',
  fontWeight: 'var(--sym-weight-semibold)',
  textAlign: 'left',
  cursor: 'pointer',
};


export function AgentsIslandQuickView({
  anchorEl,
  open,
  tone,
  side,
  headerContent,
  onClose,
}: {
  anchorEl: HTMLElement | null;
  open: boolean;
  /** The pill's current colour — the surface tints to match so it reads as the pill's own panel. */
  tone: 'blue' | 'orange';
  /** Which way the panel opens — declared by the mount (see `AgentsIslandBadge`). */
  side: IslandSide;
  /** The pill's own inner content, rendered as the panel's header row so the two are one surface. */
  headerContent: ReactNode;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const { openModal, closeModal } = useTaskSidePanel();
  const router = useRouter();
  const basePath = useBasePath();
  const island = useIslandState();
  const state = useIslandQuickView();
  const teamState = useMyAgents();
  const team = teamState.status === 'ready' ? teamState.team.agents : [];
  // The Maestro's display name — the user-renameable assistant name, same source as the rest of the
  // chrome — for working rows whose task carries no agentId.
  const maestro = useAgentName();
  // A maestro-action row opens the MAIN CHAT — the surface's existing open-chat affordance: the chat is
  // always mounted (FloatingChat) and expanding it is a plain imperative call, no navigation.
  const { maximizeChat } = useActiveConversation();
  // A fullscreen chat rides along across navigation and would COVER the page we send the reader to (the URL
  // keeps `chat=fullscreen`), so every navigation out of the card floats the chat to the side dock first
  // (minimizes to the bubble on narrow) — the same thing the island pill's own tap does. Without it, a row
  // or CTA tapped from inside the chat looked like it did nothing.
  const leaveChat = useLeaveChatOnNavigate();
  // ~20s re-read of the tasks list + the aggregate while the card is up; stops on close and unmount.
  useIslandOpenRefresh(open);
  const panelRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; offset: number; pillW: number; pillH: number } | null>(null);
  const [mounted, setMounted] = useState(false);
  const [shown, setShown] = useState(false);
  // The body's natural height (rows + footer), so max-height eases 1:1 with the real drop.
  const [bodyH, setBodyH] = useState(0);

  const place = useCallback(() => {
    if (!anchorEl) return;
    const r = anchorEl.getBoundingClientRect();
    // Overlay the pill exactly, then grow AWAY from the side it sits on: a LEFT island pins its left edge and
    // opens rightwards, a RIGHT one pins its right edge and opens leftwards, and a CENTRED one holds its
    // midpoint and opens symmetrically. Native derives this from the pill's midpoint across a phone screen;
    // here the mount declares it, because a web pill inset by the content gutter can sit mid-viewport while
    // plainly belonging to the right-hand edge.
    //
    // The offset is whichever edge is PINNED, so the growing width moves the OTHER edge and the shape starts
    // exactly on the pill. Pinning the wrong one is what made a right-hand island appear a panel-width away
    // and slide over: with `left` fixed, `width: pill → panel` grows the right edge away from the pill.
    const mid = r.left + r.width / 2;
    const raw = side === 'left' ? r.left : side === 'right' ? window.innerWidth - r.right : mid - PANEL_W / 2;
    // Never past either edge — the same clamp native applies, so a pill near a corner still lands on screen.
    const room = Math.max(EDGE_GAP, window.innerWidth - PANEL_W - EDGE_GAP);
    const offset = side === 'center' ? mid : Math.min(Math.max(raw, EDGE_GAP), room);
    setPos({ top: r.top, offset, pillW: r.width, pillH: r.height });
  }, [anchorEl, side]);

  useLayoutEffect(() => {
    if (open) place();
  }, [open, place]);

  // Re-anchoring updates state on every scroll frame, so the listener effect below must not depend on a
  // caller's inline closure — it would tear its listeners down and re-register them each frame.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);
  const close = useCallback(() => onCloseRef.current(), []);
  // Closing because the PILL left the viewport skips the close curve entirely. The curve collapses the panel
  // back onto its anchor, and there is no anchor on screen to collapse onto — so animating it just parks a
  // pill-shaped capsule over the page content for the length of the animation, which is the stray island
  // reported while scrolling. Everything else (a tap, Escape, a row) still gets the full morph.
  const vanish = useCallback(() => {
    setShown(false);
    setMounted(false);
    onCloseRef.current();
  }, []);

  useEffect(() => {
    if (open) {
      setMounted(true);
      let raf2 = 0;
      const raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(() => setShown(true));
      });
      return () => {
        cancelAnimationFrame(raf1);
        cancelAnimationFrame(raf2);
      };
    }
    setShown(false);
    const id = window.setTimeout(() => setMounted(false), CLOSE_MS);
    return () => window.clearTimeout(id);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    // `pointerdown`, so a tap outside dismisses on a touch screen at the same moment a click does on a mouse.
    const onDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (!panelRef.current?.contains(target) && !anchorEl?.contains(target)) close();
    };
    const release = pushModalEscape(close);
    const onKey = (e: KeyboardEvent) => handleModalEscape(e);
    document.addEventListener('pointerdown', onDown);
    window.addEventListener('keydown', onKey, true);
    return () => {
      release();
      document.removeEventListener('pointerdown', onDown);
      window.removeEventListener('keydown', onKey, true);
    };
  }, [open, anchorEl, close]);

  // The panel is the PILL'S OWN surface, so it RE-ANCHORS as the page moves rather than being left behind.
  // Gated on `mounted`, NOT on `open`, because the close curve collapses the panel back ONTO its anchor: stop
  // tracking the moment it starts closing and the rest of the scroll strands it, which is the second island
  // seen mid-page — the pill is already back while a capsule finishes shrinking where the pill used to be.
  // Scrolling the panel's own list is not the page moving and is ignored.
  useEffect(() => {
    if (!mounted) return undefined;
    let frame = 0;
    const onReflow = (e?: Event) => {
      if (e?.type === 'scroll') {
        const target = e.target as Node | null;
        if (target && panelRef.current?.contains(target)) return;
      }
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const r = anchorEl?.getBoundingClientRect();
        // Nothing on screen left to anchor to, so there is nothing to animate towards either.
        if (!r || r.bottom <= 0 || r.top >= window.innerHeight) {
          vanish();
          return;
        }
        place();
      });
    };
    window.addEventListener('scroll', onReflow, true);
    window.addEventListener('resize', onReflow);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onReflow, true);
      window.removeEventListener('resize', onReflow);
    };
  }, [mounted, anchorEl, vanish, place]);

  // Keep the measured body height current — including a loading→ready swap while open.
  useLayoutEffect(() => {
    const el = bodyRef.current;
    if (!el) return undefined;
    const measure = () => setBodyH(el.scrollHeight);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [state.status, mounted]);

  if (!mounted || !pos || state.status === 'error') return null;
  const loading = state.status === 'loading';
  const rows = state.status === 'ready' ? state.data.rows : [];
  // Action-required tasks the rows do not stand for, and the total they add up to — the orange card's own
  // arithmetic, all three numbers out of the one list (see below on why the header is a different fetch).
  const moreWaiting = state.status === 'ready' ? state.data.moreCount : 0;
  const working = state.status === 'ready' ? state.data.working : { actionRows: [], rows: [], moreCount: 0, workingCount: 0, workingAgents: 0 };
  const agentName = (id?: string): string | undefined => (id ? team.find((a) => a.id === id)?.name : undefined);

  // A tab's destination on the account-wide Tasks list. `?tab=` takes the camelCase union value
  // `parseBucketParam` accepts — `in-progress` is the BI token only, and sending it here would be
  // silently ignored and land on the default tab.
  const openTasksTab = (tab: AgentTaskBucket, buttonName: string) => {
    logPageClickBi({ page_name: 'agents-island-quickview', button_name: buttonName });
    leaveChat();
    onClose();
    void router.push({ pathname: '/tasks', query: { tab } }, `${basePath}/tasks?tab=${tab}`);
  };

  // Open the row in the shared modal — the SAME drill-in the "All tasks" list opens (TaskCard →
  // useTaskSidePanel.openModal) — rather than navigating to the /tasks/[id] page. Closing the island
  // first, then opening the modal, leaves the current page untouched, so dismissing the modal returns
  // here rather than to a pushed page.
  //
  // An inbound outreach has no AgentTask behind it, so there is nothing for a task detail to read; it
  // opens its THREAD in place, exactly as the list card does. The full row is taken from the account-wide
  // cache the rows were built from (the thread needs its messages, which the pure row shape doesn't
  // carry) — a read, not a new observer, and it is populated by definition since this row came from it.
  const onOpen = (task: QuickViewRow) => {
    // A collapsed origin fold has several tasks behind it and the peek cannot expand in place, so it opens
    // the account-wide Tasks list on the fold's own tab — where the same rows sit under the same collapsed
    // header (`TaskOriginGroup`), one expand away.
    if (task.runCount > 1) {
      const tab = bucketOfTask(task);
      logPageClickBi({ page_name: 'agents-island-quickview', button_name: 'open_routine_group', task_id: task.id });
      leaveChat();
      onClose();
      void router.push({ pathname: '/tasks', query: { tab } }, `${basePath}/tasks?tab=${tab}`);
      return;
    }
    const outreach = task.isOutreach
      ? queryClient.getQueryData<WebTask[]>(queryKeys.tasks(undefined))?.find((t) => t.id === task.id)
      : undefined;
    // `open_task` / `open_outreach` — the SAME src:172 button_name the Tasks list's own card reports for
    // the same action (`TaskCard`), deliberately NOT the island's local kebab style. src:172 is a
    // web-only event whose vocabulary is snake_case, and a query for `open_task` that excludes island
    // opens undercounts the action rather than failing visibly. (The generic src:198 event is unaffected
    // either way — `logPageClickBi` kebabs `button_name` into `componentName`, which is what joins to the
    // phones' `kebabCase` output, so both spellings already emitted `open-task` there.)
    logPageClickBi({
      page_name: 'agents-island-quickview',
      button_name: outreach ? 'open_outreach' : 'open_task',
      task_id: task.id,
    });
    onClose();
    openModal(outreach ? <OutreachThreadModal task={outreach} /> : <TaskDetailModal taskId={task.id} />, task.id);
  };

  // The pending-decision strip: "{n} tasks need your action ›" on the island's waiting fill, through to
  // the Waiting tab. It has TWO jobs. Inside the BLUE card it is the co-signal the blue pill no longer
  // carries. Inside the ORANGE card it is the EMPTY STATE — see the orange body below for why a card that
  // filters to nothing must still say the header's number and offer a way to the tasks behind it.
  const waitingStrip = (count: number) => (
    <button type="button" style={WAITING_STRIP} onClick={() => openTasksTab('waiting', 'open-bucket-tab')}>
      <TriangleAlert size={16} aria-hidden style={{ flexShrink: 0, color: 'var(--island-waiting-border)' }} />
      <span style={{ flex: 1, minWidth: 0 }}>
        {t(count === 1 ? 'agentsIsland.quickView.needActionOne' : 'agentsIsland.quickView.needAction', { count })}
      </span>
      <ChevronRight size={16} aria-hidden className="dir-flip" />
    </button>
  );

  // The OPEN panel sits one shade lighter than the closed pill in LIGHT mode, and takes the pill's own
  // deep fill in DARK — the phones' `islandQuickView*Bg` exactly. So the surface lifts as it opens
  // instead of the panel and the pill reading as one flat block of colour.
  //
  // These are the island's OWN roles, not the generic badge tokens. A badge fill LIGHTENS in dark
  // (Badge/Fill/In Progress #E1E9FB → #A0B8F2) while the ink on it flips to near-white, which is what
  // made the task titles vanish into the card. See the island block in globals.css.
  //
  // It CROSSES between the two rather than switching: the colour rides the same leg as the height (see the
  // transitions above), which is native's `lerp(pillFill, quickViewBg, heightP)`. Switching outright read as a
  // glitch — on close the panel finished collapsing and the colour then jumped back a beat later.
  const openTint = tone === 'orange'
    ? 'var(--island-quickview-waiting-bg)'
    : 'var(--island-quickview-in-progress-bg)';
  const pillFill = tone === 'orange' ? 'var(--island-waiting-bg)' : 'var(--island-in-progress-bg)';
  const tint = shown ? openTint : pillFill;
  // Same split as the pill: the state colour draws the panel's EDGE, never its text. Everything written on
  // the tinted surface is `Text & Icon/Primary` (black), so the open panel reads like the closed pill.
  const edge = tone === 'orange' ? 'var(--island-waiting-border)' : 'var(--island-in-progress-border)';
  const reduce = prefersReducedMotion();
  // The panel's box is the pill's footprint, but the header sits INSIDE the panel's edge — so its own box is
  // that footprint less both borders. Given the pill's full height it starts a pixel low, and the pill's text
  // drops 1px the moment the panel opens and hops back as it closes.
  const headerH = pos.pillH - PANEL_BORDER * 2;

  return createPortal(
    <div
      ref={panelRef}
      role="dialog"
      // The dialog announces the rows THIS tone actually renders — blue the live runs AND the Maestro's
      // own actions above them, orange the action-needed ones — so a screen reader hears the same body a
      // sighted reader sees.
      //
      // The SENTENCE varies by tone too, not just the count. It used to be `agents.tasksHeading` for both,
      // so the orange card announced "3 Tasks your agents are working on" over a body of action-needed
      // rows under a pill reading "11 Tasks waiting on you" (observed live). A screen-reader user was told
      // the agents were busy at the exact moment the card existed to say the opposite — that the work had
      // stopped and was waiting on them.
      aria-label={
        tone === 'blue'
          ? t('agents.tasksHeading', { count: working.actionRows.length + working.rows.length })
          : t(rows.length === 1 ? 'agentsIsland.quickView.needActionOne' : 'agentsIsland.quickView.needAction', { count: rows.length })
      }
      style={{
        position: 'fixed',
        top: pos.top,
        ...(side === 'right' ? { right: pos.offset } : { left: pos.offset }),
        // Centred: hold the MIDPOINT rather than an edge, so the width grows evenly on both sides.
        transform: side === 'center' ? 'translateX(-50%)' : undefined,
        // Above the chat surface (50) and its resize handle (60) — the panel is portalled to <body>, so in
        // fullscreen chat a lower layer put it BEHIND the chat and clicking the pill appeared to do nothing
        // (the pill hides itself while open, so it just vanished). Still far below a real modal (1000), which
        // has to cover this when a row opens a task.
        zIndex: 70,
        boxSizing: 'border-box',
        // Stretch (width) → drop (max-height). Closed = the pill footprint (header only); open = the panel.
        width: shown ? PANEL_W : pos.pillW,
        maxWidth: 'calc(100vw - 16px)',
        maxHeight: shown ? pos.pillH + bodyH : pos.pillH,
        overflow: 'hidden',
        background: tint,
        border: `${PANEL_BORDER}px solid ${edge}`,
        borderRadius: shown ? RADIUS_OPEN : pos.pillH / 2,
        boxShadow: shown ? SHADOW_LIFT : SHADOW_SHUT,
        transition: reduce ? 'none' : shown ? OPEN_TR : CLOSE_TR,
      }}
    >
      {/* The header IS the pill: its content pinned to the trailing edge so it never slides as the shape
          widens. Clicking it closes the panel (the pill's own toggle). */}
      <div
        onClick={onClose}
        style={{
          height: headerH,
          display: 'flex',
          alignItems: 'center',
          justifyContent: HEADER_JUSTIFY[side],
          gap: 8,
          // Sits on the card's 20px side inset (Figma 13523-215388).
          padding: `0 ${PANEL_PAD}px`,
          cursor: 'pointer',
          color: INK,
        }}
      >
        {headerContent}
      </div>

      {/* The body drops beneath the header and its content fades + slides in last. */}
      <div
        ref={bodyRef}
        style={{
          width: PANEL_W,
          maxWidth: '100%',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          gap: CONTENT_GAP,
          // The content starts 48px down from the panel's top edge, whatever the pill measured — so the
          // header's own height is spent out of that inset rather than added to it.
          padding: `${Math.max(0, CONTENT_TOP - headerH)}px ${PANEL_PAD}px ${PANEL_PAD}px`,
          opacity: shown ? 1 : 0,
          transform: shown ? 'translateY(0)' : 'translateY(-8px)',
          transition: reduce ? 'none' : shown ? BODY_OPEN_TR : BODY_CLOSE_TR,
        }}
      >
        {/* The top nav pill row is GONE (both tints) — it repeated what the card shows. Tab navigation
            now comes from the overflow row, the orange strip, and the footer buttons. */}

        {/* Each tone lists what its own pill claims. BLUE = the agents-at-work rows: one row per WORKING
            task (`runInFlight === true` only), newest activity first, capped to three. ORANGE = the
            ACTION-REQUIRED rows and nothing else — one per open QUESTION (over every task blocked on it,
            `groupWaitingCards`) and one per waiting task that has no question — the header says "{n} Action needed", so an IN_PROGRESS
            row or a folded "2 Done" deck underneath it is the card contradicting its own heading, which is
            exactly what was reported.

            THE HEADER AND THE ROWS COME FROM TWO DIFFERENT FETCHES and that is deliberate. The header
            count is `island.waitingCount`, summed from the per-workspace aggregate; the rows are the
            account-wide `/api/tasks` list. The card TRUSTS THE LIST for its body, because the list is the
            only source that can NAME a task — an aggregate can say "1" but cannot produce the row, and a
            card must never invent one. So the residual failure mode is bounded to "shows fewer than it
            counted" (a lagging list under a fresh aggregate), never "counts one and lists three unrelated
            things": every row that can appear here is action-required by construction. When the list has
            NOTHING action-required to show, the body falls back to the strip below rather than a blank
            box — it repeats the header's own number and hands the reader the Waiting tab, where the tasks
            the aggregate is counting actually live.

            A blue card whose loaded list holds no working rows yet keeps the loading presentation rather
            than inventing rows, for the same reason. Native's card does not scroll at all: the rows simply
            fit; the cap here is a viewport guard for a short window, not a design height. */}
          {tone === 'blue' ? (
            // Figma 17339-98544: rows in a 14px-gap column with a 0-height hairline BETWEEN them, so the
            // divider sits in a 14/14 gutter and the rows are flush to the card's 20px inset (no row padding).
            // NOT inside a ScrollArea: blue is bounded to ≤3 rows, and a scroller would clip the leftmost
            // avatar's heartbeat at its flush edge. Orange keeps the ScrollArea (its list can be longer).
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {loading || (working.actionRows.length === 0 && working.rows.length === 0) ? (
                [0, 1, 2].map((i) => (
                  <Fragment key={i}>
                    {i > 0 && <div aria-hidden style={{ ...HAIRLINE, height: 0 }} />}
                    <SkeletonRow />
                  </Fragment>
                ))
              ) : (
                <>
                  {/* Maestro-action rows FIRST — the Maestro personally at work on non-task jobs (a Wixel
                      render, a free-run loop). They share the three-row cap with the task rows below. */}
                  {working.actionRows.map((action, i) => (
                    <Fragment key={`action-${action.kind}-${i}`}>
                      {i > 0 && <div aria-hidden style={{ ...HAIRLINE, height: 0 }} />}
                      <MaestroActionRowButton
                        action={action}
                        agentLabel={maestro.short}
                        onOpen={() => {
                          logPageClickBi({ page_name: 'agents-island-quickview', button_name: 'open_maestro_action' });
                          onClose();
                          maximizeChat();
                        }}
                      />
                    </Fragment>
                  ))}
                  {working.rows.map((row: QuickViewTask, i: number) => (
                    <Fragment key={row.id}>
                      {working.actionRows.length + i > 0 && <div aria-hidden style={{ ...HAIRLINE, height: 0 }} />}
                      <WorkingRowButton
                        task={row}
                        agentLabel={agentName(row.agentId) ?? maestro.short}
                        onOpen={(task) => onOpen({ ...task, runCount: 1 })}
                      />
                    </Fragment>
                  ))}
                </>
              )}
            </div>
          ) : (
            <ScrollArea style={{ maxHeight: 'min(70vh, 560px)' }}>
              {/* Figma 17339-142828: rows in a 14px-gap column with a 0-height hairline BETWEEN them, so the
                  divider sits in a 14/14 gutter (was a bare border-top with no gap — cramped). */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {(loading ? [0, 1, 2] : rows).map((row, i) => (
                  <Fragment key={typeof row === 'number' ? row : row.id}>
                    {i > 0 ? <div style={HAIRLINE} aria-hidden /> : null}
                    {typeof row === 'number' ? (
                      <SkeletonRow />
                    ) : (
                      <QuickViewRowButton
                        task={row}
                        agentName={agentName(row.agentId) ?? maestro.short}
                        onOpen={onOpen}
                        onOpenSharedFile={(fileId, fileName) => {
                          logPageClickBi({ page_name: 'agents-island-quickview', button_name: 'open_shared_file' });
                          // Through the SAME host every other drill-in from this card uses. The card
                          // dismisses on the next outside click, so a viewer owned by the card would be
                          // torn down with it — `openModal` lives above and outlives the dismissal.
                          onClose();
                          // `closeModal` and not `back`: the card is already dismissed, so there is
                          // nothing underneath to go back TO — closing the file is leaving.
                          openModal(
                            <SharedFileViewer fileId={fileId} fileName={fileName} onClose={closeModal} />,
                            `shared-file:${fileId}`,
                          );
                        }}
                      />
                    )}
                  </Fragment>
                ))}
              </div>
            </ScrollArea>
          )}

        {/* The orange card's overflow: the CARDS the rows above do not stand for — one per open question,
            one per waiting task with no question (`moreCount` from the builder), so the rows and this line
            add up to the number the header states. The tasks a question card stands for are NOT counted
            here: they are already on screen, named in that card's "Waiting on this answer" list. It leaves
            for the Waiting tab, where all of them are. */}
        {tone === 'orange' && !loading && moreWaiting > 0 && (
          // Figma 17339-142828: a full-width SOLID (Light-skin / white) DS button with a trailing ↗ — not a
          // dashed ghost row. marginTop:4 over the body's 20 gap = the frame's 24 rows-region → button.
          <div style={{ display: 'flex', marginTop: 4 }}>
            <Button
              color="light"
              fullWidth
              endIcon={<ArrowUpRight size={16} aria-hidden className="dir-flip" />}
              style={{ justifyContent: 'space-between' }}
              onClick={() => openTasksTab('waiting', 'island_quick_view_see_more')}
            >
              {t('workspace.tasks.moreWaiting', { count: moreWaiting })}
            </Button>
          </div>
        )}

        {/* The orange card's EMPTY state — the list carries nothing action-required. Two readings, and
            they need opposite things said. The aggregate still counts something → the list is behind it,
            so repeat the header's number and point at the tab that has the tasks (never a blank card, and
            never a row invented to match the count). The aggregate agrees there is nothing → say so
            plainly; the pill is about to leave this state anyway. */}
        {tone === 'orange' && !loading && rows.length === 0 && (
          // ⚠️ TASK-counted, not card-counted, and so are the blue card's co-signal below: both say
          // "{count} tasks need your action" and both open the task TAB, which lists tasks. The orange
          // header counts CARDS (one per question); these two count what they name. iOS and Android split
          // it the same way — see `AgentsQuickView`/`AgentsIslandQuickView` there.
          island.waitingTaskCount > 0 ? (
            waitingStrip(island.waitingTaskCount)
          ) : (
            <span style={{ color: INK_2, fontSize: 14, lineHeight: '18px', letterSpacing: '-0.02em' }}>
              {t('workspace.agents.empty.waiting')}
            </span>
          )
        )}

        {/* Blue CTAs (Figma 17339-98544): the "see more" overflow and the waiting co-signal, as two
            stacked full-width buttons — white then orange — each with a trailing ArrowUpRight, 12 apart.
            "See more tasks in progress" drops its count (design). The co-signal is the blue card's
            standing waiting voice (the blue pill carries no orange pip). */}
        {tone === 'blue' && !loading && (working.moreCount > 0 || island.waitingTaskCount > 0) && (
          // +4 over the body's 20px gap = 24 rows-region → buttons-region (Figma); footer stays 20 below.
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 4 }}>
            {working.moreCount > 0 && (
              <Button
                color="light"
                fullWidth
                endIcon={<ArrowUpRight size={16} aria-hidden className="dir-flip" />}
                style={{ justifyContent: 'flex-start' }}
                onClick={() => openTasksTab('inProgress', 'island_quick_view_see_more')}
              >
                {t('agentsIsland.quickView.seeMore')}
              </Button>
            )}
            {island.waitingTaskCount > 0 && (
              <Button
                color="waiting"
                fullWidth
                endIcon={<ArrowUpRight size={16} aria-hidden className="dir-flip" />}
                style={{ justifyContent: 'flex-start' }}
                onClick={() => openTasksTab('waiting', 'open-bucket-tab')}
              >
                {t(island.waitingTaskCount === 1 ? 'agentsIsland.quickView.needActionOne' : 'agentsIsland.quickView.needAction', {
                  count: island.waitingTaskCount,
                })}
              </Button>
            )}
          </div>
        )}

        {/* Footer — two underlined Text buttons, centered (Figma): the two places the card's rows come
            from and recur on. Shared by both tones. */}
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', justifyContent: 'center' }}>
          <TextButton
            href="/tasks"
            size="small"
            underline
            onClick={() => {
              logPageClickBi({ page_name: 'agents-island-quickview', button_name: 'see-all-tasks' });
              leaveChat();
              onClose();
            }}
          >
            {t('agentsIsland.quickView.viewAllTasks')}
          </TextButton>
          <TextButton
            href="/routines"
            size="small"
            underline
            onClick={() => {
              logPageClickBi({ page_name: 'agents-island-quickview', button_name: 'island_quick_view_all_routines' });
              leaveChat();
              onClose();
            }}
          >
            {t('agentsIsland.quickView.allRoutines')}
          </TextButton>
        </div>
      </div>
    </div>,
    document.body,
  );
}
